import {
  SizeSelection_GroupedSizes_Group,
  SizeSelection_Size,
  SizeSelection_SizeRuns_SizeRun,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {
  ButtonTabs,
  COLORS,
  Dropdown,
  DropdownBody,
  DropdownBodyAlignment,
  NumberBadge,
  SimpleTabs,
  SizeCheckboxList,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc from 'styled-components';

export const SizeDropdownList = ({
  sizes,
  sizeRuns,
  groupedSizes,
  selectedSizeIds,
  onSizeSelect,
  ref: forwardedRef,
}: {
  sizes?: SizeSelection_Size[];
  sizeRuns?: SizeSelection_SizeRuns_SizeRun[];
  groupedSizes?: SizeSelection_GroupedSizes_Group[];
  label?: string;
  selectedSizeIds: number[];
  onSizeSelect: (id: number) => void;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const {t} = useTranslation();
  const {
    isOpen: isSizeSelectionOpen,
    cancel: cancelSizeSelection,
    open: openSizeSelection,
  } = useOpenedStateManager('sold-out-size-selection');

  return (
    <>
      <StyledDropdown
        onClose={cancelSizeSelection}
        open={isSizeSelectionOpen}
        onOpenerClick={openSizeSelection}
        dropdownBodyAlign={DropdownBodyAlignment.LEFT}
        animationDuration={10}
        dropdownBodyWidth="350px"
        ref={forwardedRef}
        isActive={selectedSizeIds.length > 0}
        data-testid="sizeSelectionDropdown"
        openerContent={
          <TriggerWapper>
            {t('Select size')}
            {selectedSizeIds.length > 0 ? (
              <NumberBadge count={selectedSizeIds.length} size="small" />
            ) : null}
          </TriggerWapper>
        }
        bodyContent={
          <List data-testid="sizeOptionList">
            {sizes?.length ? (
              <SizeCheckboxList
                sizes={sizes}
                selectedSizeIds={selectedSizeIds}
                onSizeSelect={onSizeSelect}
              />
            ) : null}
            {sizeRuns?.length ? (
              <StyledSimpleTabs>
                {sizeRuns?.map((sizeRun, index) => {
                  return (
                    <SizeCheckboxList
                      label={sizeRun.label}
                      sizes={sizeRun.sizes}
                      selectedSizeIds={selectedSizeIds}
                      onSizeSelect={onSizeSelect}
                      key={index}
                    />
                  );
                })}
              </StyledSimpleTabs>
            ) : null}
            {groupedSizes?.length ? (
              <ButtonTabs>
                {groupedSizes?.map((group, index) => {
                  return (
                    <Group label={group.label} key={index}>
                      {group.content?.$case === 'sizes' && (
                        <SizeCheckboxList
                          sizes={group.content.sizes.sizes}
                          selectedSizeIds={selectedSizeIds}
                          onSizeSelect={onSizeSelect}
                        />
                      )}
                      {group.content?.$case === 'sizeRuns' && (
                        <StyledSimpleTabs>
                          {group.content.sizeRuns.sizeRuns.map(
                            (sizeRun, index) => {
                              return (
                                <SizeCheckboxList
                                  label={sizeRun.label}
                                  sizes={sizeRun.sizes}
                                  selectedSizeIds={selectedSizeIds}
                                  onSizeSelect={onSizeSelect}
                                  key={index}
                                />
                              );
                            },
                          )}
                        </StyledSimpleTabs>
                      )}
                    </Group>
                  );
                })}
              </ButtonTabs>
            ) : null}
          </List>
        }
      />
    </>
  );
};

const StyledSimpleTabs = sc(SimpleTabs)`
  > button {
    color: ${COLORS.Black};
  }
`;

const Group = sc.div<{label?: string}>``;
const TriggerWapper = sc.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledDropdown = linariaStyled(Dropdown)`
  display: flow-root;

  > button {
    border: solid 1px ${COLORS.Grey3};
    height: 48px;
    padding-left: 16px;
    justify-content: space-between;
  }

  & ${DropdownBody}, & ul {
    width: 100%;
    max-width: 100%;
    max-height: 240px;
    overflow: auto;
  }
`;

const List = sc.ul`
  flex: 1;
  margin-top: 0;
  padding: 0;
  list-style: none;
`;
